import React from 'react';
import './PrivacyPolicy.css';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="privacy-page">
      <div className="privacy-container">
        <div className="privacy-header">
          <h1>Privacy Policy</h1>
          <p><strong>Effective Date:</strong> July 22, 2026</p>
          <p>
            Welcome to <strong>AstroML</strong>, operated by <strong>Zenith India</strong>,
            Maharashtra, India.
          </p>
          <p>
            Your privacy is important to us. This Privacy Policy explains how we collect,
            use, disclose, and safeguard your information when you use the AstroML mobile
            application and website.
          </p>
          <div className="privacy-info-box">
            <strong>Website:</strong>{' '}
            <a href="https://astroml.in">https://astroml.in</a>
          </div>
        </div>

        <h2>1. Information We Collect</h2>
        <p>Depending on how you use AstroML, we may collect the following information:</p>

        <h3>Personal Information</h3>
        <ul>
          <li>Name</li>
          <li>Email Address</li>
          <li>Phone Number (if provided)</li>
          <li>Profile Information</li>
        </ul>

        <h3>Birth Details</h3>
        <p>To generate personalized astrology reports, we may collect:</p>
        <ul>
          <li>Date of Birth</li>
          <li>Time of Birth</li>
          <li>Place of Birth</li>
          <li>Gender (Optional)</li>
        </ul>
        <p>These details are used solely to provide astrology-related services.</p>

        <h3>Device & Usage Information</h3>
        <ul>
          <li>Device Model</li>
          <li>Android Version</li>
          <li>IP Address</li>
          <li>App Version</li>
          <li>Crash Reports</li>
          <li>Diagnostics</li>
          <li>Usage Statistics</li>
        </ul>

        <h3>Payment Information</h3>
        <p>
          If you purchase premium services, payments are securely processed by
          Google Play or authorized payment providers.
        </p>
        <p><strong>We do not store your debit card, credit card, or banking details.</strong></p>

        <h2>2. How We Use Your Information</h2>
        <ul>
          <li>Generate personalized astrology predictions.</li>
          <li>Create horoscope reports.</li>
          <li>Provide customer support.</li>
          <li>Improve our services.</li>
          <li>Protect against fraud and abuse.</li>
          <li>Maintain account security.</li>
          <li>Send service-related notifications.</li>
          <li>Comply with applicable laws.</li>
        </ul>

        <h2>3. Sharing of Information</h2>
        <p>We do not sell your personal information.</p>
        <p>We may share limited information with trusted service providers such as:</p>
        <ul>
          <li>Cloud Hosting Providers</li>
          <li>Analytics Providers</li>
          <li>Crash Reporting Services</li>
          <li>Payment Processors</li>
          <li>Government Authorities when legally required</li>
        </ul>

        <h2>4. Data Security</h2>
        <p>We use reasonable technical and organizational safeguards including:</p>
        <ul>
          <li>HTTPS Encryption</li>
          <li>Secure Cloud Infrastructure</li>
          <li>Restricted Employee Access</li>
          <li>Authentication & Authorization Controls</li>
          <li>Security Monitoring</li>
        </ul>
        <p>
          Although we strive to protect your information, no method of transmission over
          the Internet is 100% secure.
        </p>

        <h2>5. Data Retention</h2>
        <p>
          We retain personal information only for as long as necessary to provide our
          services, comply with legal obligations, resolve disputes, and enforce our
          agreements.
        </p>
        <p>
          You may request deletion of your account and associated personal data by
          contacting us.
        </p>

        <h2>6. Your Rights</h2>
        <p>You may have the right to:</p>
        <ul>
          <li>Access your personal information.</li>
          <li>Correct inaccurate information.</li>
          <li>Request deletion of your account.</li>
          <li>Withdraw consent where applicable.</li>
          <li>Request a copy of your stored information.</li>
        </ul>

        <h2>7. Children's Privacy</h2>
        <p>
          AstroML is not intended for children under the age of 13.
          We do not knowingly collect personal information from children.
        </p>

        <h2>8. Third-Party Services</h2>
        <p>The app may use trusted third-party services including:</p>
        <ul>
          <li>Google Play Services</li>
          <li>Firebase Analytics</li>
          <li>Firebase Crashlytics</li>
          <li>Firebase Cloud Messaging</li>
          <li>Google Sign-In (if enabled)</li>
          <li>Google Play Billing</li>
        </ul>
        <p>Each provider maintains its own privacy policy.</p>

        <h2>9. App Permissions</h2>
        <p>Depending on the features used, AstroML may request permissions including:</p>
        <ul>
          <li>Internet Access</li>
          <li>Notifications</li>
          <li>Storage (if required)</li>
        </ul>
        <p>Permissions are requested only when necessary for app functionality.</p>

        <h2>10. Cookies</h2>
        <p>
          Our website may use cookies and similar technologies to improve user experience,
          analyze website traffic, and remember preferences.
        </p>

        <h2>11. International Data Transfers</h2>
        <p>
          If our infrastructure or third-party service providers operate outside your
          country, your information may be transferred and processed in accordance with
          applicable privacy laws.
        </p>

        <h2>12. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy periodically.
          Any changes will be posted on this page with an updated effective date.
        </p>

        <h2>13. Contact Us</h2>
        <div className="privacy-info-box">
          <p><strong>Company:</strong> Zenith India</p>
          <p><strong>Website:</strong> <a href="https://astroml.in">https://astroml.in</a></p>
          <p><strong>Email:</strong> <a href="mailto:zenithindia0007@gmail.com">zenithindia0007@gmail.com</a></p>
          <p><strong>CEO:</strong> Ashish Surana</p>
          <p><strong>Location:</strong> Maharashtra, India</p>
        </div>

        <h2>Google Play Data Safety</h2>
        <p>
          AstroML collects only the information necessary to provide personalized
          astrology services. Personal information such as your name, email address,
          and birth details may be collected to generate astrology reports.
          Diagnostic and usage information may also be collected to improve app
          performance. Payment information is securely handled by Google Play or
          authorized payment providers and is never stored by AstroML. Users may request
          deletion of their account and associated data by contacting us at{' '}
          <a href="mailto:zenithindia0007@gmail.com">zenithindia0007@gmail.com</a>.
        </p>

        <div className="privacy-footer">
          © 2026 Zenith India. All Rights Reserved.
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
